import { useCallback } from "react";
import { Pressable, StyleSheet } from "react-native";
import type { DateData } from "react-native-calendars";
import type { BasicDayProps } from "react-native-calendars/src/calendar/day/basic";
import { BaseText } from "~/components/layout/text";
import { QendoIcon } from "~/components/QendoIcon";

import { Space } from "~/constants/sizing";
import { LineHeights } from "~/constants/typography";
import { useTheme } from "~/providers/ThemeProvider";

type DayItemProps = {
    date?: DateData;
    onPress?: (date: DateData) => void;
} & Omit<BasicDayProps, "date">;

const MARKER_SIZE = 20;
const DAY_SIZE = LineHeights.base;
export const DAY_ITEM_HEIGHT = Space.xx + DAY_SIZE + Space.xs + MARKER_SIZE;

export const DayPickerItem = ({ marking, date, state, onPress, onLongPress }: DayItemProps) => {
    const { colours, background } = useTheme();
    const onDayPress = useCallback(() => onPress?.(date), [onPress, date]);
    const onDayLongPress = useCallback(() => onLongPress?.(date), [onLongPress, date]);

    const disabled = state === "disabled";
    const marked = Boolean(marking?.marked);
    const selected = Boolean(marking?.selected);
    const colour =
        (disabled && colours.text.quaternary) ||
        ((selected || marked) && colours.text["inverse-primary"]) ||
        colours.text.secondary;

    return (
        <Pressable style={styles.container} disabled={disabled} onPress={onDayPress} onLongPress={onDayLongPress}>
            <BaseText weight="semibold" align="center" colour={disabled ? "quaternary" : "primary"}>
                {date?.day}
            </BaseText>
            <QendoIcon
                style={[
                    styles.icon,
                    background.primary,
                    marked && { backgroundColor: colours.tracking.prediction },
                    selected && { backgroundColor: colours.tracking.selected },
                    disabled && background.secondary,
                ]}
                name={selected || marked ? "checkmark" : "circle-lg-outline"}
                color={colour}
                size={MARKER_SIZE}
            />
        </Pressable>
    );
};

const styles = StyleSheet.create({
    container: {
        justifyContent: "flex-start",
        alignItems: "center",
        gap: Space.xs,
        paddingTop: Space.xx,
        height: DAY_ITEM_HEIGHT,
    },
    icon: {
        borderRadius: MARKER_SIZE,
    },
});
